// components/ui/Select.jsx - Shadcn style Select
import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";



import Icon from '../AppIcon';

const Select = ({ 
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  error,
  required = false,
  disabled = false,
  loading = false,
  className = "",
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef?.current && !selectRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options?.find(option => option?.value === value);

  const handleSelect = (optionValue) => {
    onChange?.(optionValue);
    setIsOpen(false);
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      
      <div className="relative" ref={selectRef}>
        <button
          type="button"
          onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
          disabled={disabled || loading}
          className={`
            w-full px-3 py-2 border rounded-lg bg-background text-left
            ${error 
              ? 'border-destructive focus:border-destructive focus:ring-destructive/20' :'border-border focus:border-primary focus:ring-primary/20'
            }
            ${disabled || loading 
              ? 'opacity-50 cursor-not-allowed' :'hover:border-primary/50 focus:outline-none focus:ring-2'
            }
            ${className}
            transition-all duration-200
          `}
          {...props}
        >
          <div className="flex items-center justify-between">
            <span className={`${
              value && selectedOption 
                ? 'text-foreground' 
                : 'text-muted-foreground'
            }`}>
              {loading ? 'Loading...' : (selectedOption?.label || placeholder)}
            </span>
            
            <div className="flex items-center space-x-2">
              {loading && (
                <Icon name="Loader2" size={16} className="animate-spin text-muted-foreground" />
              )}
              <Icon 
                name="ChevronDown" 
                size={16} 
                className={`text-muted-foreground transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`} 
              />
            </div>
          </div>
        </button>

        {/* Dropdown Options */}
        {isOpen && !loading && (
          <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {options?.length > 0 ? (
              options?.map((option, index) => (
                <button
                  key={`${option?.value}-${index}`}
                  type="button"
                  onClick={() => handleSelect(option?.value)}
                  className={`
                    w-full px-3 py-2 text-left hover:bg-muted transition-colors
                    ${option?.value === value 
                      ? 'bg-primary/10 text-primary font-medium' :'text-foreground'
                    }
                    ${index === 0 ? 'rounded-t-lg' : ''}
                    ${index === options?.length - 1 ? 'rounded-b-lg' : ''}
                  `}
                >
                  {option?.label}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No options available
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <span className="text-sm text-destructive flex items-center space-x-1">
          <Icon name="AlertCircle" size={14} />
          <span>{error}</span>
        </span>
      )}
    </div>
  );
};

Select.displayName = "Select";

export default Select;